import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface DivisionModel {
    fields: {
        image: string;
        title: string;
        description: string;
        downLoadPDF: string;
        PdfLink: string;
        pageLinkLabel: string;
        pageLink: LinkFieldValue;
        linkType: "downloadPDF" | "redirectPage";
    };
}
const enum fields {
    divisions = "divisions",
    maintitle = "maintitle",
    title = "title",
    downLoadPDF = "downLoadPDF",
    pageLinkLabel = "pageLinkLabel",
    PdfLink = "PdfLink",
    linkType = "linkType",
    description = "description",
    image = "image",
    mainDescription = "mainDescription",
    buttonBackgroundColor = "buttonBackgroundColor",
    buttonLabelColor = "buttonLabelColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.maintitle]: string;
        [fields.mainDescription]: string;
        [fields.description]: string;
        [fields.divisions]: DivisionModel[];
        [fields.PdfLink]: string;
        [fields.downLoadPDF]: string;
        [fields.buttonBackgroundColor]: string;
        [fields.buttonLabelColor]: string;
    };
}

export interface CENIndustryStyles {
    mainParentContainer?: InjectableCss;
    container?: InjectableCss;
    mainTitle?: TypographyProps;
    titleStyles?: TypographyProps;
    contentContainer?: InjectableCss;
    mainDescriptionStyles?: TypographyProps;
    imageStyles?: InjectableCss;
    descriptionStyles?: InjectableCss;
    imageContainer?: InjectableCss;
}

export const cenIndustryStyles: CENIndustryStyles = {
    mainParentContainer: {
        css: css`
            position: relative;
            margin-bottom: 81px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 33px;
                    `,
                    css`
                        margin-bottom: 33px;
                    `,
                    css`
                        margin-bottom: 55px;
                    `,
                ])}
        `,
    },
    container: {
        css: css`
            display: flex;
            margin-top: 56px;
            width: 100%;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                        align-items: center;
                    `,
                ])}
        `,
    },
    contentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 50%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                        align-items: center;
                    `,
                ])}
        `,
    },
    mainTitle: {
        variant: "h1",
        css: css`
            font-size: 56px;
            font-weight: 800;
            line-height: 64px;
            margin-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 56px;
                        font-weight: 800;
                        line-height: 64px;
                    `,
                    css`
                        font-size: 56px;
                        font-weight: 800;
                        line-height: 64px;
                    `,
                    css`
                        font-size: 56px;
                        font-weight: 800;
                        line-height: 64px;
                    `,
                ])}
        `,
    },
    titleStyles: {
        variant: "h2",
        css: css`
            font-size: ${TYPOGRAPHY.size.xxlarge} !important;
            font-weight: ${TYPOGRAPHY.weight.bold} !important;
            line-height: 40px !important;
            margin-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.largest} !important;
                        line-height: 36px !important;
                        margin-top: 24px !important;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.largest} !important;
                        line-height: 36px !important;
                        margin-top: 24px !important;
                    `,
                    css`
                        margin-top: 24px !important;
                    `,
                ])}
        `,
    },
    mainDescriptionStyles: {
        css: css`
            p {
                font-size: 18px;
                font-weight: 400;
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getProp("theme.typography.p.color")};
                margin-top: 24px;
            }
        `,
    },
    descriptionStyles: {
        css: css`
            margin-top: 24px;
            p {
                font-size: 16px;
                font-style: normal;
                font-weight: 400;
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css``,
                        css``,
                        css`
                            text-align: center;
                        `,
                    ])}
            }
        `,
    },

    imageContainer: {
        css: css`
            width: 44%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 79%;
                    `,
                ])}
        `,
    },
    imageStyles: {
        css: css`
            width: 100%;
            object-fit: contain;
            aspect-ratio: 1.776/1;
            height: auto;
        `,
    },
};

const styles = cenIndustryStyles;

export const CENIndustry: FC<OwnProps> = ({ fields }) => {
    const buttonStyles = {
        css: css`
            margin-top: 32px;
            border-radius: 24px;
            background-color: ${fields.buttonBackgroundColor};
            width: max-content;
            padding: 12px 24px;
            > span {
                font-size: 18px;
                font-weight: 600;
                line-height: 24px;
                color: ${fields.buttonLabelColor} !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: ${TYPOGRAPHY.size.base};
                            line-height: ${TYPOGRAPHY.lineHeight.base};
                        `,
                        css`
                            font-size: ${TYPOGRAPHY.size.base};
                            line-height: ${TYPOGRAPHY.lineHeight.base};
                        `,
                        null,
                    ])}
            }
        `,
    };
    return (
        <StyledWrapper {...styles.mainParentContainer}>
            <Typography as="h1" {...styles.mainTitle}>
                {fields.maintitle}
            </Typography>
            <Typography as="p" {...styles.mainDescriptionStyles}>
                {parse(transformContentHtml(fields.mainDescription), parserOptions)}
            </Typography>
            {fields.divisions.map(item => {
                const { url } = useGetLink(item.fields.pageLink);
                return (
                    <>
                        <StyledWrapper {...styles.container}>
                            <StyledWrapper {...styles.imageContainer}>
                                <Img src={item.fields.image} altText={item.fields.title} {...styles.imageStyles} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.contentContainer}>
                                <Typography as="h2" {...styles.titleStyles}>
                                    {item.fields.title}
                                </Typography>
                                {item.fields.description && (
                                    <Typography as="p" {...styles.descriptionStyles}>
                                        {parse(transformContentHtml(item.fields.description), parserOptions)}
                                    </Typography>
                                )}
                                {item.fields.linkType === "downloadPDF" && item.fields.downLoadPDF.length > 0 ? (
                                    <Link {...buttonStyles} href={item.fields.PdfLink} target="_blank">
                                        {item.fields.downLoadPDF}
                                    </Link>
                                ) : (
                                    item.fields.linkType === "redirectPage" &&
                                    item.fields.pageLinkLabel.length > 0 && (
                                        <Link href={url} style={{ marginTop: "14px" }}>
                                            {item.fields.pageLinkLabel}
                                        </Link>
                                    )
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENIndustry,
    definition: {
        group: "Basic",
        displayName: "CEN Industry",
        fieldDefinitions: [
            {
                name: fields.maintitle,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.mainDescription,
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.buttonLabelColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                fieldType: "General",
                displayName: "Button Label Color",
            },
            {
                name: fields.buttonBackgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#8F0446",
                fieldType: "General",
                displayName: "Button Background Color",
            },
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                displayName: "Add list",
                fieldDefinitions: [
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                    },
                    {
                        name: fields.description,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.image,
                        editorTemplate: "ImagePickerField",
                        displayName: "Image",
                        defaultValue: "",
                    },
                    {
                        name: fields.linkType,
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "DownloadPDF",
                        options: [
                            {
                                displayName: "Download PDF",
                                value: "downloadPDF",
                            },

                            {
                                displayName: "Redirect to Page",
                                value: "redirectPage",
                            },
                        ],
                    },
                    {
                        name: fields.downLoadPDF,
                        editorTemplate: "TextField",
                        displayName: "PDF Label",
                        defaultValue: "",
                        isVisible: item => item.fields.linkType === "downloadPDF",
                    },
                    {
                        name: fields.PdfLink,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: item => item.fields.linkType === "downloadPDF",
                    },
                    {
                        name: fields.pageLinkLabel,
                        editorTemplate: "TextField",
                        displayName: "Link Label",
                        defaultValue: "",
                        isVisible: item => item.fields.linkType === "redirectPage",
                    },
                    {
                        name: "pageLink",
                        displayName: "Redirection Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: item => item.fields.linkType === "redirectPage",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
